import React from 'react';
import styled from "styled-components";
import {SText40W700, Text18} from "../Typography";
import {useCollection} from "../../cms/factory";
import {AsyncReveal} from "../AsyncReveal";
import NewsCard from "./NewsCard";
import {NavLink} from "react-router-dom";

interface NewsData {
    id: string;
    title: string;
    slug?: string;
    publishedAt?: string | null;
    logo1?: { url?: string | null } | null;
}

const SELECTION = `
    id
    title
    slug
    publishedAt
    logo1 { url }
`;

const ARGS = `
    where: { 
        status: { 
            equals: published 
        } 
    }, 
    orderBy: { 
        publishedAt: desc 
    }, 
    take: 3
`

const NewsSection = () => {
    const {data: news, loading, error} = useCollection<NewsData>(
        "newsItem",
        SELECTION,
        ARGS
    );

    return (
        <Section id="news">
            <Header>
                <SText40W700 as={'h2'}>News</SText40W700>
                <Link to={"/news"}>More</Link>
            </Header>

            <AsyncReveal loading={loading} error={error}>
                <Cards>
                    {(news ?? []).map(({id, title, slug, publishedAt, logo1}) => {
                        const showHeader = Boolean(publishedAt) || Boolean(logo1?.url);

                        return (
                            <Card key={id}>
                                {showHeader && (
                                    <NewsCard.Header>
                                        {publishedAt && <NewsCard.Date ISO={publishedAt}/>}
                                        {logo1?.url && (
                                            <NewsCard.Logos>
                                                <NewsCard.Logo src={logo1.url} alt="Logo 1"/>
                                            </NewsCard.Logos>
                                        )}
                                    </NewsCard.Header>
                                )}
                                <CardTitle as={'h3'}>{title}</CardTitle>
                                {slug && <CardLink to={`/news/${slug}`}>Read more</CardLink>}
                            </Card>
                        );
                    })}
                </Cards>
            </AsyncReveal>
        </Section>
    );
}

export default NewsSection;

const Link = styled(Text18).attrs({ as: NavLink })`
    &:hover {
        text-decoration: underline;
    }
`

const Section = styled.section`
    width: 100%;
    max-width: 1220px;
    margin: 0 auto;
    padding: 64px 10px;

    @media (max-width: 768px) {
        padding: 48px 10px ;
    }
`;

const Header = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 48px;

    @media (max-width: 768px) {
        margin-bottom: 32px;
    }
`;

const Cards = styled.div`
    display: flex;
    gap: 24px;
    overflow: auto;
`;

const Card = styled(NewsCard)`
    width: 100%;
    max-width: 384px;
    min-width: 275px;
    height: 275px;
    gap: 10px;
`

const CardTitle = styled(NewsCard.Title)`
    @media (max-width: 768px) {
        overflow: hidden;
        display: -webkit-box;
        -webkit-line-clamp: 4;
        -webkit-box-orient: vertical;
    }
`;

const CardLink = styled(Text18).attrs({ as: NavLink })`
    margin-top: auto;
    width: fit-content;
    text-decoration: underline;
`;
